import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

const C = {
  ink: '#0B0E17', surface: '#1E2235', border: '#2A2E45',
  muted: '#8892A4', gold: '#F0B429', teal: '#00C9B1',
  coral: '#FF6B6B', text: '#E8EDF5', sub: '#C5CDD8',
};

const PASSWORD_RULES = [
  { key: 'length', text: 'At least 8 characters', test: (password) => password.length >= 8 },
  { key: 'upper', text: 'At least 1 uppercase letter', test: (password) => /[A-Z]/.test(password) },
  { key: 'lower', text: 'At least 1 lowercase letter', test: (password) => /[a-z]/.test(password) },
  { key: 'special', text: 'At least 1 special character', test: (password) => /[^A-Za-z\d]/.test(password) },
  { key: 'number', text: 'At least 1 number', test: (password) => /\d/.test(password) },
];

const ROLE_LABELS = {
  admin: 'Admin',
  manager: 'Manager',
  cashier: 'Cashier',
  staff: 'Staff',
};

function normalizeEmail(value) {
  return String(value || '').trim().toLowerCase();
}

const inputStyle = {
  width: '100%',
  padding: '10px 13px',
  background: 'rgba(255,255,255,0.04)',
  border: `1.5px solid ${C.border}`,
  borderRadius: 10,
  fontSize: '0.875rem',
  color: C.text,
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.72rem',
  fontWeight: 700,
  color: C.muted,
  marginBottom: 6,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

function FormGroup({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

export default function InviteAccept() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, login, logout, updateUser } = useAuth();

  const token = searchParams.get('token');

  const [step, setStep] = useState('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [invite, setInvite] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [authTab, setAuthTab] = useState('login');
  const [form, setForm] = useState({ name: '', phone: '', email: '', password: '', confirmPassword: '' });
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  const passwordChecks = useMemo(
    () => PASSWORD_RULES.map((rule) => ({ ...rule, met: rule.test(form.password) })),
    [form.password]
  );
  const passwordValid = passwordChecks.every((rule) => rule.met);
  const passwordsMatch = form.password === form.confirmPassword;
  const inviteEmail = invite?.email || '';
  const userEmail = normalizeEmail(user?.email);
  const inviteEmailMismatch = Boolean(user && inviteEmail && userEmail !== inviteEmail);

  useEffect(() => {
    if (!token) {
      setErrorMsg('No invitation token found in the URL.');
      setStep('error');
      return;
    }

    let cancelled = false;

    async function loadInvite() {
      try {
        const { data } = await api.get('/workspaces/invite/preview', { params: { token } });
        if (cancelled) return;
        setInvite(data);
        setWorkspace(data.workspace);
        setAuthTab(data.accountState === 'existing' ? 'login' : 'register');
        setForm((current) => ({
          ...current,
          email: data.email || current.email,
        }));
        setStep('auth');
      } catch (err) {
        if (cancelled) return;
        setErrorMsg(err.response?.data?.error || 'Failed to load invitation.');
        setStep('error');
      }
    }

    loadInvite();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!invite || !user || inviteEmailMismatch || step === 'accepting' || step === 'success') {
      return;
    }

    acceptInvite();
  }, [invite, user, inviteEmailMismatch, step]);

  const acceptInvite = async () => {
    setStep('accepting');
    setAuthError('');

    try {
      const { data } = await api.post('/workspaces/invite/accept', { token });
      setWorkspace(data.workspace);
      if (data.user) {
        updateUser(data.user);
      }
      setStep('success');
      setTimeout(() => navigate('/team'), 2500);
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to accept invitation.';
      setAuthError(message);
      setErrorMsg(message);
      if (err.response?.data?.code === 'INVITE_EMAIL_MISMATCH') {
        setStep('auth');
      } else {
        setStep('error');
      }
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);

    try {
      if (authTab === 'login') {
        const { data } = await api.post('/auth/login', {
          email: inviteEmail,
          password: form.password,
        });
        login(data.token, data.refreshToken, data.user);
      } else {
        if (!passwordValid || !passwordsMatch) {
          setAuthError('Please meet the password requirements and confirm your password.');
          return;
        }

        const { data } = await api.post('/auth/register-invite', {
          token,
          name: form.name,
          phone: form.phone,
          email: inviteEmail,
          password: form.password,
        });
        login(data.token, data.refreshToken, data.user);
        setWorkspace(invite.workspace);
        setStep('success');
        setTimeout(() => navigate('/team'), 2500);
      }
    } catch (err) {
      const data = err.response?.data;
      setAuthError(data?.error || 'Authentication failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const set = (field) => (e) => setForm((current) => ({ ...current, [field]: e.target.value }));

  const inviteSummary = invite ? (
    <div
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '14px 16px',
        marginBottom: 20,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: '0.74rem', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Workspace
          </div>
          <div style={{ color: C.text, fontWeight: 700 }}>{invite.workspace?.name}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '0.74rem', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Role
          </div>
          <div style={{ color: C.gold, fontWeight: 700 }}>{ROLE_LABELS[invite.role] || invite.role}</div>
        </div>
      </div>
      <div style={{ fontSize: '0.82rem', color: C.sub }}>
        Invitation for <strong>{invite.email}</strong>
      </div>
    </div>
  ) : null;

  const card = (children) => (
    <div
      style={{
        minHeight: '100vh',
        background: C.ink,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Cabinet Grotesk', sans-serif",
        padding: '24px 16px',
      }}
    >
      <div
        style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 20,
          padding: '40px 36px',
          maxWidth: 460,
          width: '100%',
          boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
        }}
      >
        <div style={{ marginBottom: 28, textAlign: 'center' }}>
          <span style={{ fontSize: '1.3rem', fontWeight: 800, color: C.text }}>Opti</span>
          <span style={{ fontSize: '1.3rem', fontWeight: 800, color: C.gold }}>Ka</span>
        </div>
        {children}
      </div>
    </div>
  );

  if (step === 'loading' || step === 'accepting') {
    return card(
      <>
        {inviteSummary}
        <div
          style={{
            width: 48,
            height: 48,
            border: `4px solid ${C.border}`,
            borderTopColor: C.gold,
            borderRadius: '50%',
            margin: '0 auto 20px',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <p style={{ textAlign: 'center', color: C.muted }}>
          {step === 'accepting' ? 'Joining workspace...' : 'Loading invitation...'}
        </p>
        <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
      </>
    );
  }

  if (step === 'success') {
    return card(
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: 'rgba(0,201,177,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            fontSize: '1.6rem',
            color: C.teal,
          }}
        >
          ✓
        </div>
        <h2 style={{ margin: '0 0 8px', color: C.teal, fontSize: '1.25rem', fontWeight: 800 }}>You have joined</h2>
        <p style={{ margin: '0 0 20px', color: C.sub }}>
          Your account is now part of <strong>{workspace?.name}</strong>.
        </p>
        <p style={{ margin: 0, fontSize: '0.85rem', color: C.muted }}>Redirecting to the team page...</p>
      </div>
    );
  }

  if (step === 'error') {
    return card(
      <div style={{ textAlign: 'center' }}>
        {inviteSummary}
        <div
          style={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: 'rgba(255,107,107,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            fontSize: '1.3rem',
            fontWeight: 700,
            color: C.coral,
          }}
        >
          !
        </div>
        <h2 style={{ margin: '0 0 8px', color: C.coral, fontSize: '1.2rem', fontWeight: 800 }}>Invitation error</h2>
        <p style={{ margin: '0 0 24px', color: C.sub, fontSize: '0.9rem' }}>{errorMsg}</p>
        <button
          onClick={() => navigate('/')}
          style={{ background: C.gold, color: C.ink, border: 'none', borderRadius: 10, padding: '10px 24px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          Go to Home
        </button>
      </div>
    );
  }

  return card(
    <>
      <h2 style={{ margin: '0 0 4px', fontSize: '1.2rem', color: C.text, textAlign: 'center', fontWeight: 800 }}>Join workspace</h2>
      <p style={{ margin: '0 0 24px', color: C.muted, fontSize: '0.9rem', textAlign: 'center' }}>
        {authTab === 'login'
          ? 'Sign in with the invited email to join this business.'
          : 'Set your password once and join this business right away.'}
      </p>

      {inviteSummary}

      {inviteEmailMismatch ? (
        <div
          style={{
            padding: '14px 16px',
            borderRadius: 12,
            background: 'rgba(255,107,107,0.08)',
            border: '1px solid rgba(255,107,107,0.25)',
            color: C.coral,
          }}
        >
          <p style={{ margin: '0 0 12px', fontSize: '0.9rem' }}>
            You are currently signed in as <strong>{user.email}</strong>, but this invitation is for <strong>{inviteEmail}</strong>.
          </p>
          <button
            onClick={logout}
            style={{
              background: C.gold,
              color: C.ink,
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Sign out and continue
          </button>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 20, background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: '12px 14px', border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: '0.74rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
              {authTab === 'login' ? 'Existing account' : 'New staff account'}
            </div>
            <div style={{ fontSize: '0.88rem', color: C.sub }}>
              {authTab === 'login'
                ? 'This invited email already has an OptiKa account. Sign in to join the workspace.'
                : 'Create the staff password here and the account will join the workspace immediately.'}
            </div>
          </div>

          {authError && (
            <div style={{
              padding: '12px 14px',
              background: 'rgba(255,107,107,0.08)',
              border: '1px solid rgba(255,107,107,0.25)',
              borderRadius: 10,
              color: C.coral,
              fontSize: '0.85rem',
              marginBottom: 16,
            }}>
              {authError}
            </div>
          )}

          <form onSubmit={handleAuth}>
            {authTab === 'register' && (
              <>
                <FormGroup label="Full Name">
                  <input style={inputStyle} placeholder="Jane Doe" value={form.name} onChange={set('name')} required />
                </FormGroup>
                <FormGroup label="Telephone Number">
                  <input style={inputStyle} type="tel" placeholder="+233 24 123 4567" value={form.phone} onChange={set('phone')} required />
                </FormGroup>
              </>
            )}

            <FormGroup label="Invited Email">
              <input style={{ ...inputStyle, opacity: 0.6, cursor: 'not-allowed' }} type="email" value={inviteEmail} disabled readOnly />
            </FormGroup>

            <FormGroup label="Password">
              <input style={inputStyle} type="password" placeholder="Enter your password" value={form.password} onChange={set('password')} required minLength={8} />
              {authTab === 'register' && (
                <div style={{ marginTop: 8, padding: '10px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: `1px solid ${C.border}` }}>
                  <p style={{ fontSize: 11, color: C.muted, fontWeight: 600, marginBottom: 6 }}>
                    Password requirements
                  </p>
                  {passwordChecks.map((rule) => (
                    <div key={rule.key} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                      <span style={{ fontSize: 12, color: rule.met ? C.teal : C.muted }}>{rule.met ? '✓' : '○'}</span>
                      <span style={{ fontSize: 12, color: rule.met ? C.teal : C.sub }}>{rule.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </FormGroup>

            {authTab === 'register' && (
              <FormGroup label="Confirm Password">
                <input style={inputStyle} type="password" placeholder="Re-enter your password" value={form.confirmPassword} onChange={set('confirmPassword')} required minLength={8} />
                {form.confirmPassword.length > 0 && !passwordsMatch && (
                  <p style={{ marginTop: 8, fontSize: '0.8rem', color: C.coral }}>Passwords do not match.</p>
                )}
              </FormGroup>
            )}

            <button
              type="submit"
              disabled={authLoading || (authTab === 'register' && (!passwordValid || !passwordsMatch))}
              style={{
                width: '100%',
                justifyContent: 'center',
                padding: 12,
                background: C.gold,
                color: C.ink,
                border: 'none',
                borderRadius: 10,
                fontWeight: 700,
                fontSize: '0.9rem',
                fontFamily: 'inherit',
                cursor: authLoading ? 'not-allowed' : 'pointer',
                opacity: authLoading ? 0.7 : 1,
              }}
            >
              {authLoading ? 'Please wait...' : authTab === 'login' ? 'Sign In and Join' : 'Set Password and Join'}
            </button>
          </form>
        </>
      )}
    </>
  );
}
